"use client";

import { useEffect } from "react";

const KEY = "__asset_reload_count";
const PARAM = "_assetRetry";

// Webpack chunk + native dynamic-import failures (all browsers), same fix
// (reload/cache-bust). Excludes bare "Failed to fetch" — that also fires for
// app data requests.
const WEBPACK_CHUNK = /Loading (CSS )?chunk \d+ failed/i;
const DYNAMIC_IMPORT =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

export const isAssetLoadError = (error: unknown): boolean => {
  const e = error as { name?: string; message?: string } | null;
  if (!e) return false;
  const message = e.message ?? "";
  return e.name === "ChunkLoadError" || WEBPACK_CHUNK.test(message) || DYNAMIC_IMPORT.test(message);
};

const classify = (error: unknown): "webpack-chunk" | "dynamic-import" => {
  const e = error as { name?: string; message?: string } | null;
  if (e?.name === "ChunkLoadError" || WEBPACK_CHUNK.test(e?.message ?? "")) return "webpack-chunk";
  return "dynamic-import";
};

const report = (error: unknown, extra: Record<string, unknown> = {}): void => {
  const nr = window.newrelic;
  if (!nr?.noticeError) return;

  const conn = (navigator as Navigator & { connection?: { effectiveType?: string; saveData?: boolean } }).connection;

  nr.noticeError(error instanceof Error ? error : new Error(String(error)), {
    assetLoadErrorHandled: true,
    navigatorOnLine: navigator.onLine,
    connectionType: conn?.effectiveType ?? "unknown",
    saveData: conn?.saveData ?? false,
    documentVisibility: document.visibilityState,
    wasDiscarded: (document as Document & { wasDiscarded?: boolean }).wasDiscarded ?? false,
    referrerPolicy: (document as Document & { referrerPolicy?: string }).referrerPolicy ?? "unknown",
    ...extra,
  });
};

// Shared by the window listeners and error boundary so one failure seen via
// both paths doesn't burn two retries.
let handling = false;

// True once any asset error is handled this load, so a page that keeps failing
// can't reset the retry counter and loop forever.
let errorSeenThisLoad = false;

/**
 * Recovery ladder for an asset-load failure; logs to New Relic each time.
 * Returns `true` when retries are exhausted (caller shows a fallback).
 *   1st -> reload | 2nd -> reload + cache-bust param | 3rd -> stop
 */
export const recover = (error: unknown, source: "window" | "error-boundary"): boolean => {
  if (typeof window === "undefined") return false;

  errorSeenThisLoad = true;

  const reloads = Number(sessionStorage.getItem(KEY) || "0");

  if (reloads >= 2) {
    report(error, { errorKind: classify(error), source, assetLoadErrorFallbackShown: true });
    return true;
  }

  if (handling) return false;
  report(error, { errorKind: classify(error), source });

  handling = true;
  if (reloads === 0) {
    sessionStorage.setItem(KEY, "1");
    window.location.reload();
  } else {
    sessionStorage.setItem(KEY, "2");
    const next = new URL(window.location.href);
    next.searchParams.set(PARAM, Date.now().toString());
    window.location.replace(next.toString());
  }

  return false;
};

/**
 * Mounted once at the app root. Catches asset-load failures from window
 * `error` / `unhandledrejection` events and drives {@link recover}; render-time
 * failures are handled by the exported `ErrorBoundary`. Renders nothing.
 */
export function AssetLoadErrorHandler() {
  useEffect(() => {
    // Cosmetic only: drop the cache-bust param from the address bar.
    const url = new URL(window.location.href);
    if (url.searchParams.has(PARAM)) {
      url.searchParams.delete(PARAM);
      window.history.replaceState({}, "", url.toString());
    }

    // Reset the counter only after the page stays healthy briefly — resetting
    // eagerly let a persistently failing chunk loop forever.
    const resetTimer = window.setTimeout(() => {
      if (!errorSeenThisLoad) sessionStorage.removeItem(KEY);
    }, 10000);

    const onError = (event: ErrorEvent) => {
      if (isAssetLoadError(event.error) || isAssetLoadError({ message: event.message })) {
        recover(event.error ?? new Error(event.message), "window");
      }
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      if (isAssetLoadError(event.reason)) recover(event.reason, "window");
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.clearTimeout(resetTimer);
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
